import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useSupplierCrmOrgStore } from '../../../store/supplierCrmOrg.store';
import { useAuth } from '../../../contexts/AuthContext';
import { tasksService, type Task, type TaskInput } from '../../../services/projectManagement/tasksService';
import { projectsService, type Project } from '../../../services/projectManagement/projectsService';
import { dependenciesService, type TaskDependency } from '../../../services/projectManagement/dependenciesService';
import { pmSectionsApiService, type ProjectSection } from '../../../services/projectManagement/pmSectionsApiService';
import milestonesService, { type Milestone } from '../../../services/projectManagement/milestonesService';
import TaskDetailModal from '../components/TaskDetailModal';
import TaskListView, { type GroupBy as TaskListGroupBy, type SortBy as TaskListSortBy } from '../components/TaskListView';
import CalendarView from '../components/CalendarView';
import GanttChartView from '../components/GanttChartView';
import TaskTableView, { type SortDirection as TaskTableSortDirection, type SortField as TaskTableSortField, type TaskTableColumnId } from '../components/TaskTableView';
import DependencyGraph from '../components/DependencyGraph';
import PmSavedViewsControl from '../components/PmSavedViewsControl';
import PmFilterBuilder from '../components/PmFilterBuilder';
import IntakeFormsModal from '../components/IntakeFormsModal';
import ProjectSectionsModal from '../components/ProjectSectionsModal';
import { pmMembersService, buildMemberMap, getDisplayName, type ProjectManagementMember } from '../../../services/projectManagement/membersService';
import { applyPmFilters, normalizePmFilterState, type PmFilterState } from '../lib/pmFilters';
import { analyzePmSchedule, computeResizedTaskDates, computeShiftedTaskDates, planAutoPushSuccessors } from '../lib/pmScheduling';

type TaskStatus = 'backlog' | 'todo' | 'in-progress' | 'review' | 'done' | 'blocked';

const COLUMNS: { id: TaskStatus; label: string; color: string }[] = [
  { id: 'todo', label: 'To Do', color: 'border-gray-300' },
  { id: 'in-progress', label: 'In Progress', color: 'border-blue-500' },
  { id: 'review', label: 'Review', color: 'border-yellow-500' },
  { id: 'done', label: 'Done', color: 'border-green-500' },
];

const TasksTab: React.FC = () => {
  const { activeOrgId } = useSupplierCrmOrgStore();
  const { user } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [dependencies, setDependencies] = useState<TaskDependency[]>([]);
  const [members, setMembers] = useState<ProjectManagementMember[]>([]);
  const [sections, setSections] = useState<ProjectSection[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [showSectionsModal, setShowSectionsModal] = useState(false);
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'kanban' | 'list' | 'table' | 'gantt' | 'calendar' | 'dependencies'>('kanban');

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const taskId = params.get('taskId');
    if (taskId) {
      setSelectedTaskId(taskId);
    }
  }, [location.search]);

  const [taskFilters, setTaskFilters] = useState<PmFilterState>({ query: '', combinator: 'and', rules: [] });
  const [listGroupBy, setListGroupBy] = useState<TaskListGroupBy>('status');
  const [listSortBy, setListSortBy] = useState<TaskListSortBy>('due_date');
  const [listSortAsc, setListSortAsc] = useState(true);
  const [tableSortField, setTableSortField] = useState<TaskTableSortField>('due_date');
  const [tableSortDirection, setTableSortDirection] = useState<TaskTableSortDirection>('asc');
  const [tableVisibleColumns, setTableVisibleColumns] = useState<TaskTableColumnId[]>([
    'status',
    'priority',
    'assignee_id',
    'project_name',
    'due_date',
    'estimated_time',
  ]);

  // Form state
  const [formData, setFormData] = useState<Partial<TaskInput>>({
    title: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    project_id: '',
    estimated_time: 0,
    labels: [],
  });

  const fetchData = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const [tasksRes, projectsRes, membersRes, sectionsRes, milestonesRes] = await Promise.all([
        tasksService.list({
          organization_id: activeOrgId,
          project_id: selectedProject !== 'all' ? selectedProject : undefined,
        }),
        projectsService.list({ organization_id: activeOrgId }),
        pmMembersService.list(activeOrgId),
        selectedProject !== 'all'
          ? pmSectionsApiService.list({ organization_id: activeOrgId, project_id: selectedProject })
          : Promise.resolve({ data: [], error: null }),
        milestonesService.list({
          organization_id: activeOrgId,
          project_id: selectedProject !== 'all' ? selectedProject : undefined,
        }),
      ]);

      const depsRes = await dependenciesService.list({
        organization_id: activeOrgId,
        project_id: selectedProject !== 'all' ? selectedProject : undefined,
      });

      if (tasksRes.error) {
        toast.error('Failed to load tasks');
      } else {
        setTasks(tasksRes.data);
      }

      if (!membersRes.error) {
        setMembers(membersRes.data);
      } else {
        setMembers([]);
      }

      if (!sectionsRes.error) {
        setSections(sectionsRes.data || []);
      } else {
        setSections([]);
      }

      if (!milestonesRes.error) {
        setMilestones(milestonesRes.data || []);
      } else {
        setMilestones([]);
      }

      if (!projectsRes.error) {
        setProjects(projectsRes.data);
        // Auto-select first project if none selected
        if (selectedProject === 'all' && projectsRes.data.length > 0 && formData.project_id === '') {
          setFormData(prev => ({ ...prev, project_id: projectsRes.data[0].id }));
        }
      }

      if (depsRes.error) {
        setDependencies([]);
      } else {
        setDependencies(depsRes.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, selectedProject]);

  useEffect(() => {
    if (selectedProject === 'all' && listGroupBy === 'section') {
      setListGroupBy('status');
    }
  }, [listGroupBy, selectedProject]);

  const memberMap = React.useMemo(() => buildMemberMap(members), [members]);

  const taskLabelOptions = useMemo(() => {
    const s = new Set<string>();
    tasks.forEach(t => (t.labels || []).forEach(l => s.add(String(l))));
    return Array.from(s)
      .filter(Boolean)
      .sort()
      .map(l => ({ value: l, label: l }));
  }, [tasks]);

  const assigneeOptions = useMemo(() => {
    return (members || []).map(m => ({ value: m.user_id, label: getDisplayName(m) }));
  }, [members, memberMap]);

  const projectOptions = useMemo(() => {
    return (projects || []).map(p => ({ value: p.id, label: p.name }));
  }, [projects]);

  const selectedProjectObj = useMemo(() => {
    return projects.find(p => p.id === selectedProject) || null;
  }, [projects, selectedProject]);

  const taskFilterFields = useMemo(() => {
    return [
      {
        field: 'status',
        label: 'Status',
        type: 'select' as const,
        options: [
          { value: 'backlog', label: 'Backlog' },
          { value: 'todo', label: 'To Do' },
          { value: 'in-progress', label: 'In Progress' },
          { value: 'review', label: 'Review' },
          { value: 'done', label: 'Done' },
          { value: 'blocked', label: 'Blocked' },
        ],
      },
      {
        field: 'priority',
        label: 'Priority',
        type: 'select' as const,
        options: [
          { value: 'low', label: 'Low' },
          { value: 'medium', label: 'Medium' },
          { value: 'high', label: 'High' },
          { value: 'urgent', label: 'Urgent' },
        ],
      },
      {
        field: 'assignee_id',
        label: 'Assignee',
        type: 'select' as const,
        options: assigneeOptions,
      },
      {
        field: 'project_id',
        label: 'Project',
        type: 'select' as const,
        options: projectOptions,
      },
      {
        field: 'due_date',
        label: 'Due Date',
        type: 'date' as const,
      },
      {
        field: 'estimated_time',
        label: 'Estimate (hours)',
        type: 'number' as const,
      },
      {
        field: 'labels',
        label: 'Labels',
        type: 'select' as const,
        options: taskLabelOptions,
      },
    ];
  }, [assigneeOptions, projectOptions, taskLabelOptions]);

  const visibleTasks = useMemo(() => {
    return applyPmFilters(tasks, taskFilters, ['title', 'description', 'project_name']);
  }, [tasks, taskFilters]);

  const visibleDependencies = useMemo(() => {
    const visibleTaskIdSet = new Set(visibleTasks.map(t => t.id));
    return dependencies.filter(d => visibleTaskIdSet.has(d.task_id) && visibleTaskIdSet.has(d.depends_on_task_id));
  }, [dependencies, visibleTasks]);

  const scheduleAnalysis = useMemo(() => {
    return analyzePmSchedule(visibleTasks, visibleDependencies);
  }, [visibleDependencies, visibleTasks]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOrgId || !formData.project_id) return;

    try {
      const payload: TaskInput = {
        organization_id: activeOrgId,
        project_id: formData.project_id,
        title: formData.title!,
        description: formData.description,
        status: formData.status,
        priority: formData.priority,
        assignee_id: formData.assignee_id,
        estimated_time: formData.estimated_time,
        labels: formData.labels,
      };

      const { error } = await tasksService.create(payload);
      if (error) {
        toast.error(error);
        return;
      }

      toast.success('Task created successfully');
      setShowCreateModal(false);
      setFormData({
        title: '',
        description: '',
        status: 'todo',
        priority: 'medium',
        project_id: formData.project_id, // Keep selected project
        estimated_time: 0,
        labels: [],
      });
      void fetchData();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleDragStart = (task: Task) => {
    setDraggedTask(task);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (status: TaskStatus) => {
    if (!draggedTask || !activeOrgId) return;

    try {
      const { error } = await tasksService.update(draggedTask.id, activeOrgId, { status });
      if (error) {
        toast.error('Failed to update task');
        return;
      }

      // Optimistic update
      setTasks(prev =>
        prev.map(t => (t.id === draggedTask.id ? { ...t, status } : t))
      );
      setDraggedTask(null);
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'low': return '🟢';
      case 'medium': return '🟡';
      case 'high': return '🟠';
      case 'urgent': return '🔴';
      default: return '⚪';
    }
  };

  const getTasksByStatus = (status: TaskStatus) => {
    return visibleTasks.filter(task => task.status === status);
  };

  if (loading) {
    return <div className="rounded-xl bg-white p-12 text-center">Loading tasks...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* View Toggle */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-3 py-1 text-sm font-medium ${
                viewMode === 'kanban'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              📊 Kanban
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 text-sm font-medium ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              📋 List
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1 text-sm font-medium ${
                viewMode === 'table'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              📊 Table
            </button>
            <button
              onClick={() => setViewMode('gantt')}
              className={`px-3 py-1 text-sm font-medium ${
                viewMode === 'gantt'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              📈 Gantt
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1 text-sm font-medium ${
                viewMode === 'calendar'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              📅 Calendar
            </button>
            <button
              onClick={() => setViewMode('dependencies')}
              className={`px-3 py-1 text-sm font-medium ${
                viewMode === 'dependencies'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              🕸️ Dependencies
            </button>
          </div>


          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
          >
            <option value="all">All Projects</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>

          {activeOrgId && (
            <PmSavedViewsControl
              organizationId={activeOrgId}
              scope="tasks"
              getCurrentPayload={() => ({
                selectedProject,
                viewMode,
                taskFilters,
                listGroupBy,
                listSortBy,
                listSortAsc,
                tableSortField,
                tableSortDirection,
                tableVisibleColumns,
              })}
              applyPayload={(payload) => {
                const nextProject = String((payload as any)?.selectedProject || '');
                const nextViewMode = String((payload as any)?.viewMode || '');
                const nextTaskFilters = normalizePmFilterState((payload as any)?.taskFilters);
                const nextListGroupBy = String((payload as any)?.listGroupBy || '');
                const nextListSortBy = String((payload as any)?.listSortBy || '');
                const nextListSortAsc = (payload as any)?.listSortAsc;
                const nextTableSortField = String((payload as any)?.tableSortField || '');
                const nextTableSortDirection = String((payload as any)?.tableSortDirection || '');
                const nextTableVisibleColumns = (payload as any)?.tableVisibleColumns;

                if (nextProject) {
                  setSelectedProject(nextProject);
                }

                if (['kanban', 'list', 'table', 'gantt', 'calendar', 'dependencies'].includes(nextViewMode)) {
                  setViewMode(nextViewMode as any);
                }

                setTaskFilters(nextTaskFilters);

                if (['status', 'priority', 'project', 'section', 'none'].includes(nextListGroupBy)) {
                  setListGroupBy(nextListGroupBy as any);
                }
                if (['due_date', 'priority', 'created_at', 'title'].includes(nextListSortBy)) {
                  setListSortBy(nextListSortBy as any);
                }
                if (typeof nextListSortAsc === 'boolean') {
                  setListSortAsc(nextListSortAsc);
                }
                if (['title', 'status', 'priority', 'due_date', 'assignee_id', 'project_name', 'estimated_time'].includes(nextTableSortField)) {
                  setTableSortField(nextTableSortField as any);
                }
                if (['asc', 'desc'].includes(nextTableSortDirection)) {
                  setTableSortDirection(nextTableSortDirection as any);
                }

                if (Array.isArray(nextTableVisibleColumns)) {
                  const allowed: TaskTableColumnId[] = ['status', 'priority', 'assignee_id', 'project_name', 'due_date', 'estimated_time'];
                  const allowedSet = new Set<string>(allowed);
                  const filtered = nextTableVisibleColumns
                    .map((c: any) => String(c))
                    .filter((c: string) => allowedSet.has(c)) as TaskTableColumnId[];
                  const uniq = Array.from(new Set(filtered));
                  if (uniq.length > 0) {
                    setTableVisibleColumns(uniq);
                  }
                }
              }}
            />
          )}

          <div className="text-sm text-gray-600">
            {visibleTasks.length} tasks
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowIntakeModal(true)}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            📝 Intake
          </button>
          {selectedProjectObj && activeOrgId && (
            <button
              onClick={() => setShowSectionsModal(true)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              title="Manage project sections"
            >
              🧩 Sections
            </button>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
            disabled={projects.length === 0}
          >
            ➕ New Task
          </button>
        </div>
      </div>

      <PmFilterBuilder
        title="Tasks"
        placeholder="Search tasks..."
        value={taskFilters}
        onChange={setTaskFilters}
        availableFields={taskFilterFields}
      />

      {/* View Content */}
      {projects.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
          <div className="text-4xl mb-4">📋</div>
          <h3 className="text-lg font-semibold text-gray-900">No Projects Yet</h3>
          <p className="mt-2 text-sm text-gray-600">
            Create a project first, then add tasks to it
          </p>
        </div>
      ) : viewMode === 'list' ? (
        <TaskListView
          tasks={visibleTasks}
          sections={selectedProject !== 'all' ? sections : undefined}
          memberMap={memberMap}
          onTaskClick={(taskId) => setSelectedTaskId(taskId)}
          groupBy={listGroupBy}
          onGroupByChange={setListGroupBy}
          sortBy={listSortBy}
          onSortByChange={setListSortBy}
          sortAsc={listSortAsc}
          onSortAscChange={setListSortAsc}
          onStatusChange={async (taskId, status) => {
            if (!activeOrgId) return;
            const { error } = await tasksService.update(taskId, activeOrgId, { status: status as any });
            if (error) {
              toast.error('Failed to update task');
            } else {
              void fetchData();
            }
          }}
        />
      ) : viewMode === 'table' ? (
        <TaskTableView
          tasks={visibleTasks}
          memberMap={memberMap}
          onTaskClick={(taskId) => setSelectedTaskId(taskId)}
          sortField={tableSortField}
          onSortFieldChange={setTableSortField}
          sortDirection={tableSortDirection}
          onSortDirectionChange={setTableSortDirection}
          visibleColumns={tableVisibleColumns}
          onVisibleColumnsChange={setTableVisibleColumns}
          onTaskUpdate={async (taskId, updates) => {
            if (!activeOrgId) return;
            try {
              await tasksService.update(taskId, activeOrgId, updates);
              await fetchData();
              toast.success('Task updated successfully');
            } catch (error) {
              toast.error('Failed to update task');
            }
          }}
        />
      ) : viewMode === 'gantt' ? (
        <GanttChartView
          tasks={visibleTasks}
          milestones={milestones}
          onTaskClick={(taskId) => setSelectedTaskId(taskId)}
          onMilestoneClick={(milestone) => {
            toast.success(`Milestone: ${milestone.name}`);
          }}
          criticalTaskIds={scheduleAnalysis.criticalTaskIds}
          violationTaskIds={scheduleAnalysis.violationTaskIds}
          violations={scheduleAnalysis.violations}
          slackByTaskId={scheduleAnalysis.slackByTaskId}
          onTaskShiftDays={async (taskId, deltaDays) => {
            if (!activeOrgId) return;
            const task = tasks.find(t => t.id === taskId);
            if (!task) return;

            const updates = computeShiftedTaskDates(task, deltaDays);
            if (!updates.start_date || !updates.due_date) {
              toast.error('Task must have a start date or due date to reschedule');
              return;
            }

            try {
              await tasksService.update(taskId, activeOrgId, updates);

              const tasksNext = tasks.map(t => (t.id === taskId ? { ...t, ...updates } : t));
              const ripple = planAutoPushSuccessors(tasksNext, dependencies, taskId);
              for (const r of ripple) {
                await tasksService.update(r.taskId, activeOrgId, r.update);
              }

              await fetchData();
              toast.success(ripple.length > 0 ? `Task rescheduled (+${ripple.length} dependent tasks adjusted)` : 'Task rescheduled');
            } catch (error) {
              toast.error('Failed to reschedule task');
            }
          }}
          onTaskResizeDays={async (taskId, edge, deltaDays) => {
            if (!activeOrgId) return;
            const task = tasks.find(t => t.id === taskId);
            if (!task) return;

            const updates = computeResizedTaskDates(task, edge, deltaDays);
            if (!updates.start_date || !updates.due_date) {
              toast.error('Task must have a start date or due date to resize');
              return;
            }

            try {
              await tasksService.update(taskId, activeOrgId, updates);

              const tasksNext = tasks.map(t => (t.id === taskId ? { ...t, ...updates } : t));
              const ripple = planAutoPushSuccessors(tasksNext, dependencies, taskId);
              for (const r of ripple) {
                await tasksService.update(r.taskId, activeOrgId, r.update);
              }

              await fetchData();
              toast.success(ripple.length > 0 ? `Task resized (+${ripple.length} dependent tasks adjusted)` : 'Task resized');
            } catch (error) {
              toast.error('Failed to resize task');
            }
          }}
        />
      ) : viewMode === 'calendar' ? (
        <CalendarView
          tasks={visibleTasks}
          onTaskClick={(taskId) => setSelectedTaskId(taskId)}
        />
      ) : viewMode === 'dependencies' ? (
        <DependencyGraph
          tasks={visibleTasks}
          dependencies={dependencies
            .filter(d => visibleTasks.some(t => t.id === d.task_id) && visibleTasks.some(t => t.id === d.depends_on_task_id))
            .map(d => ({
              id: d.id,
              from_task_id: d.depends_on_task_id,
              to_task_id: d.task_id,
              dependency_type: d.dependency_type === 'blocked-by'
                ? 'blocked_by'
                : d.dependency_type === 'relates-to'
                ? 'related_to'
                : 'blocks',
            }))}
          criticalTaskIds={scheduleAnalysis.criticalTaskIds}
          violationTaskIds={scheduleAnalysis.violationTaskIds}
          violationEdges={scheduleAnalysis.violationEdges}
          onDependencyAdd={async (fromTaskId, toTaskId) => {
            if (!activeOrgId) return;
            const { error } = await dependenciesService.create({
              organization_id: activeOrgId,
              task_id: toTaskId,
              depends_on_task_id: fromTaskId,
              dependency_type: 'blocks',
            });

            if (error) {
              toast.error('Failed to create dependency');
              return;
            }

            void fetchData();
          }}
          onDependencyRemove={async (dependencyId) => {
            if (!activeOrgId) return;
            const { error } = await dependenciesService.delete(dependencyId, activeOrgId);
            if (error) {
              toast.error('Failed to remove dependency');
              return;
            }
            void fetchData();
          }}
        />
      ) : (
        <div className="grid grid-cols-4 gap-4">
          {COLUMNS.map(column => (
            <div
              key={column.id}
              className={`rounded-xl border-2 ${column.color} bg-gray-50 p-4`}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(column.id)}
            >
              {/* Column Header */}
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">{column.label}</h3>
                <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-gray-600">
                  {getTasksByStatus(column.id).length}
                </span>
              </div>

              {/* Tasks */}
              <div className="space-y-3">
                {getTasksByStatus(column.id).map(task => (
                  <div
                    key={task.id}
                    draggable
                    onDragStart={() => handleDragStart(task)}
                    onClick={() => setSelectedTaskId(task.id)}
                    className="cursor-pointer rounded-lg border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition-shadow"
                  >
                    {/* Task Title */}
                    <div className="mb-2 flex items-start justify-between">
                      <h4 className="text-sm font-semibold text-gray-900 flex-1">
                        {task.title}
                      </h4>
                      <span className="text-lg ml-2">
                        {getPriorityIcon(task.priority)}
                      </span>
                    </div>

                    {/* Task Description */}
                    {task.description && (
                      <p className="mb-2 text-xs text-gray-600 line-clamp-2">
                        {task.description}
                      </p>
                    )}

                    {/* Task Metadata */}
                    <div className="space-y-1">
                      {/* Project */}
                      {task.project_name && (
                        <div className="text-xs text-gray-500">
                          📁 {task.project_name}
                        </div>
                      )}

                      {/* Time estimate */}
                      {task.estimated_time > 0 && (
                        <div className="text-xs text-gray-500">
                          ⏱️ {task.estimated_time}h
                        </div>
                      )}

                      {/* Due date */}
                      {task.due_date && (
                        <div className="text-xs text-gray-500">
                          📅 {new Date(task.due_date).toLocaleDateString()}
                        </div>
                      )}

                      {task.assignee_id && (
                        <div className="text-xs text-gray-500">
                          👤 {getDisplayName(memberMap[task.assignee_id] || null)}
                        </div>
                      )}

                      {/* Labels */}
                      {task.labels && task.labels.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {task.labels.slice(0, 2).map((label, index) => (
                            <span
                              key={index}
                              className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700"
                            >
                              {label}
                            </span>
                          ))}
                          {task.labels.length > 2 && (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                              +{task.labels.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Empty state */}
                {getTasksByStatus(column.id).length === 0 && (
                  <div className="rounded-lg border-2 border-dashed border-gray-300 bg-white p-4 text-center text-sm text-gray-400">
                    Drop tasks here
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="mb-4 text-lg font-semibold">Create New Task</h3>
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium">Project *</label>
                <select
                  value={formData.project_id}
                  onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                  className="w-full rounded-lg border px-4 py-2"
                  required
                >
                  <option value="">Select project...</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium">Task Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full rounded-lg border px-4 py-2"
                  placeholder="e.g., Design login page"
                  required
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border px-4 py-2"
                  placeholder="Add details about this task..."
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label className="mb-2 block text-sm font-medium">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as TaskStatus })}
                    className="w-full rounded-lg border px-4 py-2"
                  >
                    <option value="backlog">Backlog</option>
                    <option value="todo">To Do</option>
                    <option value="in-progress">In Progress</option>
                    <option value="review">Review</option>
                    <option value="done">Done</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                    className="w-full rounded-lg border px-4 py-2"
                  >
                    <option value="low">Low 🟢</option>
                    <option value="medium">Medium 🟡</option>
                    <option value="high">High 🟠</option>
                    <option value="urgent">Urgent 🔴</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">Estimate (hours)</label>
                  <input
                    type="number"
                    value={formData.estimated_time}
                    onChange={(e) => setFormData({ ...formData, estimated_time: parseInt(e.target.value) || 0 })}
                    className="w-full rounded-lg border px-4 py-2"
                    min="0"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium">Assignee</label>
                <select
                  value={formData.assignee_id || ''}
                  onChange={(e) => setFormData({ ...formData, assignee_id: e.target.value || undefined })}
                  className="w-full rounded-lg border px-4 py-2"
                >
                  <option value="">Unassigned</option>
                  {members.map(m => (
                    <option key={m.user_id} value={m.user_id}>
                      {getDisplayName(m)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
                >
                  Create Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showIntakeModal && activeOrgId && (
        <IntakeFormsModal
          organizationId={activeOrgId}
          projects={projects}
          onClose={() => setShowIntakeModal(false)}
          onCreated={() => {
            setShowIntakeModal(false);
            void fetchData();
          }}
        />
      )}

      {showSectionsModal && activeOrgId && selectedProjectObj && (
        <ProjectSectionsModal
          organizationId={activeOrgId}
          project={selectedProjectObj}
          onClose={() => setShowSectionsModal(false)}
          onUpdated={() => {
            void fetchData();
          }}
        />
      )}

      {/* Task Detail Modal */}
      {selectedTaskId && activeOrgId && (
        <TaskDetailModal
          taskId={selectedTaskId}
          organizationId={activeOrgId}
          onClose={() => setSelectedTaskId(null)}
          onUpdate={() => {
            void fetchData();
          }}
        />
      )}
    </div>
  );
};

export default TasksTab;
